import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import {
  DlqReason,
  ErrorEventSeverity,
  ErrorEventStatus,
  SeverityCatalogSource,
} from '../../../services/api';

/** Deutsche Anzeige-Labels je Severity — auch von der Kopfzeilen-Glocke genutzt. */
export const SEVERITY_LABELS: Record<ErrorEventSeverity, string> = {
  critical: 'Kritisch',
  very_high: 'Sehr hoch',
  high: 'Hoch',
  medium: 'Mittel',
  low: 'Niedrig',
};

/** Badge-Farbklassen je Severity — auch von der Kopfzeilen-Glocke genutzt. */
export const SEVERITY_COLORS: Record<ErrorEventSeverity, string> = {
  critical: 'bg-red-100 text-red-800',
  very_high: 'bg-orange-100 text-orange-800',
  high: 'bg-yellow-100 text-yellow-800',
  medium: 'bg-blue-100 text-blue-800',
  low: 'bg-gray-100 text-gray-800',
};

/** Optionen für die Severity-Select-Filter — Wert bleibt der rohe Enum-String. */
export const SEVERITY_SELECT_OPTIONS: { value: ErrorEventSeverity; label: string }[] = [
  { value: 'critical', label: SEVERITY_LABELS.critical },
  { value: 'very_high', label: SEVERITY_LABELS.very_high },
  { value: 'high', label: SEVERITY_LABELS.high },
  { value: 'medium', label: SEVERITY_LABELS.medium },
  { value: 'low', label: SEVERITY_LABELS.low },
];

/** Type-Guard für Select-Rohwerte — vermeidet `as ErrorEventSeverity`-Casts beim Anwenden des Filters. */
export function isErrorEventSeverity(value: string): value is ErrorEventSeverity {
  return SEVERITY_SELECT_OPTIONS.some((option) => option.value === value);
}

export interface SeverityBadgeProps {
  severity: ErrorEventSeverity;
}

export function SeverityBadge({ severity }: SeverityBadgeProps) {
  return (
    <Badge className={cn(SEVERITY_COLORS[severity] ?? 'bg-gray-100 text-gray-800')}>
      {SEVERITY_LABELS[severity] ?? severity}
    </Badge>
  );
}

const STATUS_LABELS: Record<ErrorEventStatus, string> = {
  open: 'Offen',
  acknowledged: 'Quittiert',
  resolved: 'Gelöst',
};

const STATUS_DOT_COLORS: Record<ErrorEventStatus, string> = {
  open: 'bg-red-500',
  acknowledged: 'bg-amber-500',
  resolved: 'bg-green-500',
};

/** Optionen für die Status-Select-Filter — Wert bleibt der rohe Enum-String. */
export const STATUS_SELECT_OPTIONS: { value: ErrorEventStatus; label: string }[] = [
  { value: 'open', label: STATUS_LABELS.open },
  { value: 'acknowledged', label: STATUS_LABELS.acknowledged },
  { value: 'resolved', label: STATUS_LABELS.resolved },
];

/** Type-Guard für Select-Rohwerte — vermeidet `as ErrorEventStatus`-Casts beim Anwenden des Filters. */
export function isErrorEventStatus(value: string): value is ErrorEventStatus {
  return STATUS_SELECT_OPTIONS.some((option) => option.value === value);
}

export interface StatusBadgeProps {
  status: ErrorEventStatus;
}

export function StatusBadge({ status }: StatusBadgeProps) {
  return (
    <Badge variant="outline" className="gap-1.5">
      <span className={cn('inline-block h-1.5 w-1.5 rounded-full', STATUS_DOT_COLORS[status])} />
      {STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

/** Deutsche Anzeige-Labels für die Herkunft eines Katalog-Identifiers. */
const CATALOG_SOURCE_LABELS: Record<SeverityCatalogSource, string> = {
  registered: 'Registriert',
  observed: 'Beobachtet',
};

/**
 * Badge-Farbklassen je Herkunft. Bewusst indigo/amber statt einer Severity-Farbe —
 * „woher kennen wir den Identifier" darf nicht mit „wie dringend ist er" verwechselt werden.
 */
const CATALOG_SOURCE_COLORS: Record<SeverityCatalogSource, string> = {
  registered: 'bg-indigo-100 text-indigo-800',
  observed: 'bg-amber-100 text-amber-800',
};

/** Erklärtexte für den Tooltip/`title` der Herkunfts-Badge. */
const CATALOG_SOURCE_TITLES: Record<SeverityCatalogSource, string> = {
  registered: 'Im Code-Katalog deklariert (error-identifiers.constants.ts)',
  observed: 'Nur im Betrieb aufgetaucht, im Code-Katalog nicht hinterlegt',
};

/** Optionen für den Herkunfts-Filter im Severity-Tab. */
export const CATALOG_SOURCE_SELECT_OPTIONS: { value: SeverityCatalogSource; label: string }[] = [
  { value: 'registered', label: CATALOG_SOURCE_LABELS.registered },
  { value: 'observed', label: CATALOG_SOURCE_LABELS.observed },
];

/** Type-Guard für Select-Rohwerte — vermeidet `as SeverityCatalogSource`-Casts. */
export function isSeverityCatalogSource(value: string): value is SeverityCatalogSource {
  return CATALOG_SOURCE_SELECT_OPTIONS.some((option) => option.value === value);
}

export interface CatalogSourceBadgeProps {
  source: SeverityCatalogSource;
}

export function CatalogSourceBadge({ source }: CatalogSourceBadgeProps) {
  return (
    <Badge
      className={cn(CATALOG_SOURCE_COLORS[source] ?? 'bg-gray-100 text-gray-800')}
      title={CATALOG_SOURCE_TITLES[source]}
    >
      {CATALOG_SOURCE_LABELS[source] ?? source}
    </Badge>
  );
}

const DLQ_REASON_LABELS: Record<DlqReason, string> = {
  schema_violation: 'Schema-Verstoß',
  invalid_json: 'Ungültiges JSON',
  processing_failed: 'Verarbeitung fehlgeschlagen',
};

const DLQ_REASON_COLORS: Record<DlqReason, string> = {
  schema_violation: 'bg-orange-100 text-orange-800',
  invalid_json: 'bg-red-100 text-red-800',
  processing_failed: 'bg-blue-100 text-blue-800',
};

export interface DlqReasonBadgeProps {
  reason: DlqReason;
}

export function DlqReasonBadge({ reason }: DlqReasonBadgeProps) {
  return (
    <Badge className={cn(DLQ_REASON_COLORS[reason] ?? 'bg-gray-100 text-gray-800')}>
      {DLQ_REASON_LABELS[reason] ?? reason}
    </Badge>
  );
}
